import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api from '@/lib/apiClient';
import { formatApiError } from '@/lib/utils';
import { showConfirm } from '@/components/ui/GlobalConfirm';
import {
  FolderLock, Loader2, FileText, Download, Eye, Trash2,
  Search, Image, File, Film, X, Upload
} from 'lucide-react';

const fmtSize = (b) => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};
const fmtF = (s) => { try { return new Date(s).toLocaleDateString('es-AR'); } catch { return s || '—'; } };

/* ─── Icono por tipo MIME ──────────────────────────────────── */
function IconoArchivo({ mime }) {
  if (!mime) return <File className="w-5 h-5 text-[#C9A44C]" />;
  if (mime.startsWith('image/')) return <Image className="w-5 h-5 text-blue-400" />;
  if (mime.startsWith('video/')) return <Film className="w-5 h-5 text-purple-400" />;
  if (mime === 'application/pdf') return <FileText className="w-5 h-5 text-red-400" />;
  if (mime.includes('word') || mime.includes('document')) return <FileText className="w-5 h-5 text-blue-300" />;
  return <File className="w-5 h-5 text-[#C9A44C]" />;
}

/* ─── Modal preview (usa blob para evitar "Not authenticated") ── */
function ModalPreview({ evidencia, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [err, setErr] = useState('');
  const mime = evidencia.tipo_mimetype || '';
  const esImagen = mime.startsWith('image/');
  const esPDF = mime === 'application/pdf';
  const esHTML = mime === 'text/html';
  const esVideo = mime.startsWith('video/');

  useEffect(() => {
    let url = null;
    api.get(`/api/vault/evidencias/${evidencia.id}/stream`, { responseType: 'blob' })
      .then((r) => {
        url = URL.createObjectURL(r.data);
        setBlobUrl(url);
      })
      .catch(() => setErr('No se pudo cargar el archivo'))
      .finally(() => setCargando(false));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [evidencia.id]);

  const descargar = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl; a.download = evidencia.nombre; a.click();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" onClick={onClose}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10 flex-shrink-0"
        onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{evidencia.nombre}</div>
          <div className="text-[11px] text-[#888]">
            {evidencia.expediente_caratula && `${evidencia.expediente_caratula} · `}
            {fmtSize(evidencia.tamano)} · {fmtF(evidencia.fecha_creacion)}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4">
          <button onClick={(e) => { e.stopPropagation(); descargar(); }} disabled={!blobUrl}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[rgba(201,164,76,0.25)] text-[#C9A44C] rounded-lg hover:bg-[#C9A44C]/10 transition-all disabled:opacity-40">
            <Download className="w-3.5 h-3.5" /> Descargar
          </button>
          <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0] p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Contenido */}
      <div className="flex-1 overflow-hidden flex items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}>
        {cargando ? (
          <Loader2 className="w-8 h-8 animate-spin text-[#C9A44C]" />
        ) : err ? (
          <p className="text-red-400 text-sm">{err}</p>
        ) : esImagen ? (
          <img src={blobUrl} alt={evidencia.nombre}
            className="max-w-full max-h-full object-contain rounded-lg" />
        ) : esPDF ? (
          <iframe src={blobUrl} title={evidencia.nombre}
            className="w-full h-full rounded-lg border border-white/10"
            style={{ maxWidth: '900px' }} />
        ) : esHTML ? (
          <iframe src={blobUrl} title={evidencia.nombre}
            className="w-full h-full rounded-lg border border-white/10 bg-white"
            style={{ maxWidth: '900px' }} />
        ) : esVideo ? (
          <video src={blobUrl} controls className="max-w-full max-h-full rounded-lg" />
        ) : (
          <div className="text-center">
            <File className="w-16 h-16 text-[#888] mx-auto mb-4" />
            <p className="text-[#888] mb-4">Preview no disponible para este tipo de archivo</p>
            <button onClick={descargar}
              className="inline-flex items-center gap-2 text-sm px-5 py-2.5 rounded-lg text-[#070707] font-semibold"
              style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
              <Download className="w-4 h-4" /> Descargar archivo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Vault page ─────────────────────────────────────────────── */
export default function Vault() {
  const [evidencias, setEvidencias] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [preview, setPreview] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [expSubida, setExpSubida] = useState('');
  const [errSubida, setErrSubida] = useState('');

  const cargar = async () => {
    setLoading(true);
    try {
      const [v, e] = await Promise.allSettled([
        api.get('/api/vault/evidencias'),
        api.get('/api/expedientes/'),
      ]);
      if (v.status === 'fulfilled') setEvidencias(v.value.data);
      if (e.status === 'fulfilled') setExpedientes(e.value.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { cargar(); }, []);

  const subirArchivo = async (file) => {
    if (!file) return;
    if (!expSubida) { setErrSubida('Seleccioná un expediente primero'); return; }
    setErrSubida(''); setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/api/vault/evidencias?expediente_id=${expSubida}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setExpSubida('');
      cargar();
    } catch (e) {
      setErrSubida(formatApiError(e.response?.data?.detail, 'Error al subir'));
    } finally { setSubiendo(false); }
  };

  const eliminar = async (id, nombre) => {
    if (!(await showConfirm(`¿Eliminar "${nombre}"? Esta acción es irreversible.`, { titulo: 'Eliminar archivo' }))) return;
    await api.delete(`/api/vault/evidencias/${id}`);
    cargar();
  };

  const filtradas = evidencias.filter((ev) => {
    const t = `${ev.nombre || ''} ${ev.cliente_nombre || ''} ${ev.expediente_caratula || ''}`.toLowerCase();
    const ok_q = q ? t.includes(q.toLowerCase()) : true;
    const ok_t = filtroTipo ? (ev.tipo_mimetype || '').includes(filtroTipo) : true;
    return ok_q && ok_t;
  });

  // Agrupar por expediente para mejor UX
  const porExpediente = {};
  filtradas.forEach((ev) => {
    const key = ev.expediente_caratula || 'Sin expediente';
    if (!porExpediente[key]) porExpediente[key] = [];
    porExpediente[key].push(ev);
  });

  return (
    <DashboardLayout title="Bóveda de evidencias">
      {/* Panel de subida */}
      <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.15)] rounded-xl p-4 mb-5">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A44C] mb-3">Subir archivo a la bóveda</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            className="flex-1 bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C9A44C] transition-all"
            value={expSubida} onChange={(e) => { setExpSubida(e.target.value); setErrSubida(''); }}>
            <option value="" className="bg-[#141414]">Seleccionar expediente…</option>
            {expedientes.map((e) => (
              <option key={e.id} value={e.id} className="bg-[#141414]">
                {e.caratula}{e.cliente_nombre ? ` — ${e.cliente_nombre}` : ''}
              </option>
            ))}
          </select>
          <label className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg cursor-pointer font-semibold text-sm transition-all whitespace-nowrap ${
            subiendo || !expSubida
              ? 'opacity-50 cursor-not-allowed bg-[#C9A44C]/40 text-[#070707]'
              : 'text-[#070707]'
          }`}
            style={subiendo || !expSubida ? {} : { background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
            {subiendo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {subiendo ? 'Subiendo…' : 'Seleccionar archivo'}
            <input type="file" className="hidden" disabled={subiendo || !expSubida}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt,.xlsx,.xls,.html"
              onChange={(e) => subirArchivo(e.target.files[0])} />
          </label>
        </div>
        {errSubida && (
          <p className="text-red-400 text-xs mt-2">{errSubida}</p>
        )}
      </div>

      {/* Stats rápidas */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        {[
          { label: 'Total archivos', val: evidencias.length },
          { label: 'Expedientes', val: Object.keys(porExpediente).length },
          { label: 'Tamaño total', val: fmtSize(evidencias.reduce((a, e) => a + (e.tamano || 0), 0)) },
        ].map(({ label, val }) => (
          <div key={label} className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-4 text-center">
            <div className="text-xl font-light text-[#C9A44C]" style={{ fontFamily: 'Playfair Display, serif' }}>{val}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#888] mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888]" />
          <input className="w-full bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] placeholder-[#888]/50 rounded-lg px-3.5 py-2.5 text-sm pl-10 focus:outline-none focus:border-[#C9A44C] transition-all"
            placeholder="Buscar archivo, cliente o expediente..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select className="bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] rounded-lg px-3 py-2.5 text-sm focus:outline-none w-36"
          value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
          <option value="" className="bg-[#141414]">Todos</option>
          <option value="pdf" className="bg-[#141414]">PDF</option>
          <option value="image" className="bg-[#141414]">Imágenes</option>
          <option value="word" className="bg-[#141414]">Word</option>
          <option value="html" className="bg-[#141414]">HTML</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#C9A44C]" /></div>
      ) : filtradas.length === 0 ? (
        <div className="text-center py-20 text-[#666]">
          <FolderLock className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">
            {evidencias.length === 0
              ? 'La bóveda está vacía. Los archivos se suben desde la Vista 360 de cada expediente.'
              : 'Sin resultados para tu búsqueda'}
          </p>
        </div>
      ) : (
        /* Agrupado por expediente */
        <div className="space-y-5">
          {Object.entries(porExpediente).map(([caratula, evs]) => (
            <div key={caratula}>
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C9A44C]" />
                <p className="text-xs text-[#C9A44C] uppercase tracking-[0.15em] truncate">{caratula}</p>
                <span className="text-[10px] text-[#666]">({evs.length})</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {evs.map((ev) => (
                  <div key={ev.id}
                    className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-4 hover:border-[#C9A44C]/30 transition-all group">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white/[0.04] flex items-center justify-center flex-shrink-0">
                        <IconoArchivo mime={ev.tipo_mimetype} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate" title={ev.nombre}>{ev.nombre}</div>
                        <div className="text-[11px] text-[#888] truncate">{ev.cliente_nombre}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-[#666]">{fmtSize(ev.tamano)}</span>
                          <span className="text-[#555]">·</span>
                          <span className="text-[10px] text-[#666]">{fmtF(ev.fecha_creacion)}</span>
                        </div>
                      </div>
                    </div>
                    {/* Acciones */}
                    <div className="flex gap-1.5 mt-3 pt-3 border-t border-white/5">
                      <button onClick={() => setPreview(ev)}
                        className="flex-1 flex items-center justify-center gap-1.5 text-[11px] py-1.5 rounded-lg border border-[rgba(201,164,76,0.15)] text-[#C9A44C] hover:bg-[#C9A44C]/10 transition-all">
                        <Eye className="w-3.5 h-3.5" /> Ver
                      </button>
                      <button onClick={async () => {
                          try {
                            const r = await api.get(`/api/vault/evidencias/${ev.id}/stream`, { responseType: 'blob' });
                            const url = URL.createObjectURL(r.data);
                            const a = document.createElement('a');
                            a.href = url; a.download = ev.nombre; a.click();
                            setTimeout(() => URL.revokeObjectURL(url), 3000);
                          } catch {}
                        }}
                        className="flex-1 flex items-center justify-center gap-1.5 text-[11px] py-1.5 rounded-lg border border-white/10 text-[#888] hover:text-[#F8F6F0] hover:border-white/20 transition-all">
                        <Download className="w-3.5 h-3.5" /> Descargar
                      </button>
                      <button onClick={() => eliminar(ev.id, ev.nombre)}
                        className="p-1.5 rounded-lg border border-transparent text-[#666] hover:text-red-400 hover:border-red-500/25 hover:bg-red-500/10 transition-all">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview modal */}
      {preview && <ModalPreview evidencia={preview} onClose={() => setPreview(null)} />}
    </DashboardLayout>
  );
}
